import { useCallback, useEffect, useRef, useState } from 'react'
import type { QuickBooksConnectLink, QuickBooksConnectLinkPerson, ServerApiClient } from '../../../data/ServerApiClient'
import './QuickBooksConnectLinkPanel.css'
import { connectLinkMailto, connectLinkPersonLabel, looksLikeEmail } from './quickBooksConnectLink'

/**
 * The standing QuickBooks connect link for one CFO Core: show it, copy it,
 * email it, revoke it, or make a new one.
 *
 * The link is for the company's QuickBooks admin, who has no FuseGrid login.
 * The server keeps one live link per CFO Core, shared by everyone who can edit
 * it, so opening this panel twice shows the same URL. Every rule stated in the
 * "How this link works" copy is enforced in `connect_portal.rs`:
 * connect-only scope, no expiry, per-link revocation, the link acting as (and
 * dying with the edit access of) the person it is requested by, and the first
 * new company filling this CFO Core.
 *
 * "Request comes from" lets the sharer put someone else's name on the request,
 * e.g. the company's CFO when the site was set up for them. The choice is
 * limited to site members who can edit the CFO Core, because the link acts as
 * that person. Someone not yet on the site can be added in place, through the
 * same endpoint (and the same invitation email) as Manage users.
 */

type PanelState =
  | { kind: 'loading' }
  | { kind: 'ready'; link: QuickBooksConnectLink }
  | { kind: 'none' }
  | { kind: 'error'; message: string }

export type QuickBooksConnectLinkPanelProps = {
  api: ServerApiClient
  workbookId: string
  /** The CFO Core's site. Needed to add someone to the site in place;
   * without it the requester can only be picked from existing members. */
  siteSlug?: string | null
  /** Plain company name, used in the email draft. */
  companyLabel: string
  /** Make the link at once when the CFO Core has none (the connect dialog
   * does this, having just created the CFO Core for it). */
  autoCreate?: boolean
  /** Reconnecting a company that is already set up, rather than connecting it
   * for the first time. Only changes the wording. */
  reconnect?: boolean
  /** Called after the link is created or revoked, so a list can refresh. */
  onChanged?: () => void
}

const errorMessage = (err: any, fallback: string): string => {
  const raw = String(err?.message || err || '').trim()
  if (/failed to fetch/i.test(raw)) return 'FuseGrid could not reach the server. Try again in a moment.'
  if (/workbook_edit_required|forbidden|\b403\b/i.test(raw)) {
    return 'Only people who can edit this CFO Core can share its QuickBooks link.'
  }
  if (/workbook_uses_other_provider/i.test(raw)) {
    return 'This CFO Core already uses another accounting provider, so it cannot take a QuickBooks company.'
  }
  if (/requester_cannot_edit_workbook/i.test(raw)) {
    return 'That person cannot edit this CFO Core, so the link cannot ask on their behalf. Give them an editor role in Manage users.'
  }
  return fallback
}

const addPersonErrorMessage = (err: any): string => {
  const raw = String(err?.message || err || '').trim()
  if (/failed to fetch/i.test(raw)) return 'FuseGrid could not reach the server. Try again in a moment.'
  if (/\b403\b|forbidden|site admin/i.test(raw)) {
    return 'Only site admins can add people to this site. Pick someone already on it, or ask a site admin.'
  }
  if (/requester_cannot_edit_workbook/i.test(raw)) {
    return 'They were added, but cannot edit this CFO Core yet, so the link cannot ask on their behalf. Check their project access in Manage users.'
  }
  return 'Could not add them to the site. Check the email and try again.'
}

const formatWhen = (value: string | null | undefined): string => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const ADD_PERSON = '__add_person__'

export function QuickBooksConnectLinkPanel({
  api,
  workbookId,
  siteSlug,
  companyLabel,
  autoCreate = false,
  reconnect = false,
  onChanged,
}: QuickBooksConnectLinkPanelProps) {
  const [state, setState] = useState<PanelState>({ kind: 'loading' })
  const [busy, setBusy] = useState<'' | 'create' | 'revoke' | 'requester'>('')
  const [copied, setCopied] = useState(false)
  const [requesters, setRequesters] = useState<QuickBooksConnectLinkPerson[] | null>(null)
  const [addingPerson, setAddingPerson] = useState(false)
  const [newPersonEmail, setNewPersonEmail] = useState('')
  const [requesterError, setRequesterError] = useState('')
  const urlRef = useRef<HTMLInputElement | null>(null)
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  const create = useCallback(async () => {
    setBusy('create')
    try {
      const link = await api.createQuickBooksConnectLink(workbookId)
      setState({ kind: 'ready', link })
      onChangedRef.current?.()
    } catch (err: any) {
      setState({ kind: 'error', message: errorMessage(err, 'Could not create the QuickBooks link. Try again.') })
    } finally {
      setBusy('')
    }
  }, [api, workbookId])

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    setCopied(false)
    void (async () => {
      try {
        const existing = await api.getQuickBooksConnectLink(workbookId)
        if (cancelled) return
        if (existing) {
          setState({ kind: 'ready', link: existing })
          return
        }
        if (!autoCreate) {
          setState({ kind: 'none' })
          return
        }
        const link = await api.createQuickBooksConnectLink(workbookId)
        if (cancelled) return
        setState({ kind: 'ready', link })
        onChangedRef.current?.()
      } catch (err: any) {
        if (!cancelled) {
          setState({ kind: 'error', message: errorMessage(err, 'Could not load the QuickBooks link. Try again.') })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [api, autoCreate, workbookId])

  const linkReady = state.kind === 'ready'
  const loadRequesters = useCallback(async () => {
    try {
      setRequesters(await api.listQuickBooksConnectLinkRequesters(workbookId))
    } catch {
      // The picker falls back to showing the current requester only.
      setRequesters([])
    }
  }, [api, workbookId])

  useEffect(() => {
    if (!linkReady) return
    void loadRequesters()
  }, [linkReady, loadRequesters])

  const setRequester = useCallback(async (userId: string) => {
    setBusy('requester')
    setRequesterError('')
    try {
      const link = await api.createQuickBooksConnectLink(workbookId, userId)
      setState({ kind: 'ready', link })
      onChangedRef.current?.()
    } catch (err: any) {
      setRequesterError(errorMessage(err, 'Could not change who the request comes from. Try again.'))
    } finally {
      setBusy('')
    }
  }, [api, workbookId])

  const addPerson = useCallback(async () => {
    const email = newPersonEmail.trim()
    if (!siteSlug) return
    if (!looksLikeEmail(email)) {
      setRequesterError('Enter their email address.')
      return
    }
    setBusy('requester')
    setRequesterError('')
    try {
      // Same endpoint as Manage users: adds them as an editor and sends the
      // same sign-in email.
      const membership = await api.createSiteUser(siteSlug, { email, site_role: 'creator', status: 'active' })
      const link = await api.createQuickBooksConnectLink(workbookId, membership.user_id)
      setState({ kind: 'ready', link })
      setAddingPerson(false)
      setNewPersonEmail('')
      onChangedRef.current?.()
      await loadRequesters()
    } catch (err: any) {
      setRequesterError(addPersonErrorMessage(err))
      await loadRequesters()
    } finally {
      setBusy('')
    }
  }, [api, loadRequesters, newPersonEmail, siteSlug, workbookId])

  const copy = useCallback(async (url: string) => {
    let ok = false
    try {
      await navigator.clipboard.writeText(url)
      ok = true
    } catch {
      // Clipboard access can be refused (permissions, an insecure origin).
      // Selecting the text leaves the reader one keystroke from copying it.
      const input = urlRef.current
      if (input) {
        input.focus()
        input.select()
        try {
          ok = document.execCommand('copy')
        } catch {
          ok = false
        }
      }
    }
    setCopied(ok)
    if (ok) window.setTimeout(() => setCopied(false), 2400)
  }, [])

  const revoke = useCallback(async (link: QuickBooksConnectLink) => {
    const ok = window.confirm(
      'Revoke this link? It stops working at once, including for anyone partway through connecting. You can create a new link afterwards.',
    )
    if (!ok) return
    setBusy('revoke')
    try {
      await api.revokeQuickBooksConnectLink(link.id)
      setState({ kind: 'none' })
      setCopied(false)
      onChangedRef.current?.()
    } catch (err: any) {
      setState({ kind: 'error', message: errorMessage(err, 'Could not revoke the link. Try again.') })
    } finally {
      setBusy('')
    }
  }, [api])

  if (state.kind === 'loading') {
    return (
      <div className="cfo-qbo-link" aria-busy="true">
        <p className="cfo-qbo-link-note">{autoCreate ? 'Creating the link…' : 'Loading the link…'}</p>
      </div>
    )
  }

  if (state.kind === 'error' || state.kind === 'none') {
    return (
      <div className="cfo-qbo-link">
        {state.kind === 'error' ? (
          <p className="cfo-qbo-link-error" role="alert">{state.message}</p>
        ) : (
          <p className="cfo-qbo-link-note">
            There is no active QuickBooks link for this CFO Core. Create one to send to the company&rsquo;s
            QuickBooks admin.
          </p>
        )}
        <div className="cfo-qbo-link-actions">
          <button type="button" className="btn primary" onClick={() => void create()} disabled={busy !== ''}>
            {busy === 'create' ? 'Creating…' : state.kind === 'none' ? 'Create link' : 'Try again'}
          </button>
        </div>
      </div>
    )
  }

  const { link } = state
  const opened = formatWhen(link.last_opened_at)
  const lastConnected = formatWhen(link.last_connected_at)
  const activity = [
    link.created_by_email ? `Created by ${link.created_by_email}` : '',
    opened ? `last opened ${opened}` : 'not opened yet',
    link.connected_count > 0
      ? `${link.connected_count} ${link.connected_count === 1 ? 'connection' : 'connections'} made${lastConnected ? `, last on ${lastConnected}` : ''}`
      : '',
  ].filter(Boolean).join(' · ')

  const current = link.requested_by
  // The current requester is always an option, even if the list failed to
  // load or they are no longer on it.
  const people = [
    ...(requesters || []),
    ...((requesters || []).some((person) => person.user_id === current.user_id) ? [] : [current]),
  ]
  const requesterFieldId = `cfo-qbo-link-requester-${link.id}`

  return (
    <div className="cfo-qbo-link">
      <label className="cfo-qbo-link-label" htmlFor={requesterFieldId}>
        Request comes from
      </label>
      <select
        id={requesterFieldId}
        className="cfo-qbo-link-requester"
        value={addingPerson ? ADD_PERSON : current.user_id}
        disabled={busy !== '' || requesters === null}
        onChange={(e) => {
          const value = e.target.value
          setRequesterError('')
          if (value === ADD_PERSON) {
            setAddingPerson(true)
            return
          }
          setAddingPerson(false)
          if (value !== current.user_id) void setRequester(value)
        }}
      >
        {people.map((person) => (
          <option key={person.user_id} value={person.user_id}>{connectLinkPersonLabel(person)}</option>
        ))}
        {siteSlug ? <option value={ADD_PERSON}>Add someone to this site…</option> : null}
      </select>
      {addingPerson ? (
        <form
          className="cfo-qbo-link-add"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void addPerson()
          }}
        >
          <div className="cfo-qbo-link-url-row">
            <input
              type="email"
              className="cfo-qbo-link-url cfo-qbo-link-email"
              placeholder="cfo@company.com"
              aria-label="Email of the person the request comes from"
              value={newPersonEmail}
              onChange={(e) => setNewPersonEmail(e.target.value)}
              autoFocus
              disabled={busy !== ''}
            />
            <button type="submit" className="btn primary" disabled={busy !== ''}>
              {busy === 'requester' ? 'Adding…' : 'Add and use'}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={busy !== ''}
              onClick={() => {
                setAddingPerson(false)
                setNewPersonEmail('')
                setRequesterError('')
              }}
            >
              Cancel
            </button>
          </div>
          <p className="cfo-qbo-link-note">
            They&rsquo;re added to this site as an editor, and FuseGrid emails them how to sign in, the same as
            adding them in Manage users.
          </p>
        </form>
      ) : (
        <p className="cfo-qbo-link-note">
          The QuickBooks admin sees the request as coming from this person, and the connection is made in their
          name.
        </p>
      )}
      {requesterError ? <p className="cfo-qbo-link-error" role="alert">{requesterError}</p> : null}
      <label className="cfo-qbo-link-label cfo-qbo-link-label-gap" htmlFor={`cfo-qbo-link-url-${link.id}`}>
        Link for the QuickBooks admin
      </label>
      <div className="cfo-qbo-link-url-row">
        <input
          id={`cfo-qbo-link-url-${link.id}`}
          ref={urlRef}
          className="cfo-qbo-link-url"
          value={link.url}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          spellCheck={false}
        />
        <button type="button" className="btn primary" onClick={() => void copy(link.url)}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <p className="cfo-qbo-link-activity" role="status" aria-live="polite">
        {copied ? 'Link copied. Paste it into an email or message to the QuickBooks admin.' : activity}
      </p>
      <div className="cfo-qbo-link-actions">
        <a className="btn ghost" href={connectLinkMailto(link, companyLabel, reconnect)}>
          Email link
        </a>
        <button
          type="button"
          className="btn ghost cfo-qbo-link-revoke"
          onClick={() => void revoke(link)}
          disabled={busy !== ''}
        >
          {busy === 'revoke' ? 'Revoking…' : 'Revoke link'}
        </button>
      </div>
      <details className="cfo-qbo-link-help">
        <summary>How this link works</summary>
        <ul>
          <li>
            The admin doesn&rsquo;t need a FuseGrid login. The link opens a FuseGrid page where they sign in to
            QuickBooks and pick the company.
          </li>
          <li>
            The link doesn&rsquo;t expire. If QuickBooks ever needs to be reauthorized, the admin can use the same
            link again.
          </li>
          <li>
            The first company connected through the link fills this CFO Core. If the admin connects more companies,
            each one gets its own CFO Core in this site.
          </li>
          <li>
            The link can only start a QuickBooks connection. It can&rsquo;t show or change anything else in FuseGrid.
          </li>
          <li>
            The admin&rsquo;s page names the person the request comes from. The link acts for them: the QuickBooks
            connection is theirs, and FuseGrid&rsquo;s &ldquo;CFO Core is ready&rdquo; email goes to them. Changing
            the person keeps the same link.
          </li>
          <li>
            Anyone who has the link can use it, so send it only to the company&rsquo;s QuickBooks admin. It stops
            working when you revoke it, or when the person the request comes from can no longer edit this CFO Core.
          </li>
        </ul>
      </details>
    </div>
  )
}

/** The panel in a modal, for a CFO Core's row and its Manage menu. */
export function QuickBooksConnectLinkDialog({
  onClose,
  ...panel
}: QuickBooksConnectLinkPanelProps & { onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const title = panel.reconnect ? 'Reconnect QuickBooks with a link' : 'Share a QuickBooks link'
  return (
    <div className="cfo-qbo-link-backdrop" onClick={onClose}>
      <div
        className="card lead cfo-qbo-link-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="body" style={{ display: 'block' }}>
          <h3 style={{ fontSize: 'var(--fs-lg)' }}>{title}</h3>
          <p style={{ marginBottom: 'var(--s4)' }}>
            {panel.reconnect
              ? <>Send this link to whoever administers <strong>{panel.companyLabel}</strong> in QuickBooks. They sign in to QuickBooks and authorize it again. They don&rsquo;t need a FuseGrid login.</>
              : <>Send this link to whoever administers <strong>{panel.companyLabel}</strong> in QuickBooks. They sign in to QuickBooks and pick the company. They don&rsquo;t need a FuseGrid login.</>}
          </p>
          <QuickBooksConnectLinkPanel {...panel} />
          <div className="cfo-qbo-link-dialog-foot">
            <button type="button" className="btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuickBooksConnectLinkPanel
